import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import instance from "../apiInstance";
import { isFeatureTester } from "../adminEmails";
import "./ClientGuideModal.css";

/** Só dispara na home — evita repetir ao trocar de aba. */
const GUIDE_PATH = "/home";
const SESSION_DONE_KEY = "client_guide_session_done";

export default function ClientGuideModal() {
  const { token } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const [hint, setHint] = useState(null);
  const [visible, setVisible] = useState(false);
  const [tester, setTester] = useState(false);
  const fetchedRef = useRef(false);
  const timerRef = useRef(null);

  const close = useCallback(() => {
    setVisible(false);
    setHint(null);
  }, []);

  const dismiss = useCallback(async (hintId) => {
    sessionStorage.setItem(SESSION_DONE_KEY, "1");
    close();
    try {
      await instance.post("/guidance/dismiss", { hint_id: hintId });
    } catch {
      /* silencioso */
    }
  }, [close]);

  const goCta = useCallback(async (item) => {
    if (!item) return;
    sessionStorage.setItem(SESSION_DONE_KEY, "1");
    close();
    try {
      await instance.post("/guidance/complete", { hint_id: item.id });
    } catch {
      /* silencioso */
    }
    if (item.cta_path) {
      navigate(item.cta_path);
    }
  }, [close, navigate]);

  useEffect(() => {
    if (!token) {
      setTester(false);
      return undefined;
    }
    let cancelled = false;
    instance
      .get("/me")
      .then((res) => {
        if (!cancelled) {
          setTester(isFeatureTester(res.data?.user?.email));
        }
      })
      .catch(() => {
        if (!cancelled) setTester(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    close();

    if (!token || !tester) return undefined;
    if (location.pathname !== GUIDE_PATH) return undefined;
    if (sessionStorage.getItem(SESSION_DONE_KEY)) return undefined;

    timerRef.current = setTimeout(() => {
      if (fetchedRef.current) return;
      fetchedRef.current = true;

      instance
        .get("/guidance")
        .then((res) => {
          if (!res.data?.lab) return;
          const hints = res.data?.hints || [];
          if (!hints.length) return;
          if (sessionStorage.getItem(SESSION_DONE_KEY)) return;
          const next = hints[0];
          setHint(next);
          setVisible(true);
        })
        .catch(() => {});
    }, 2500);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, tester, location.pathname, close]);

  if (!tester || !visible || !hint) return null;

  return (
    <div className="client-guide-overlay" role="dialog" aria-modal="true" aria-labelledby="client-guide-title">
      <div className="client-guide-card" onClick={(e) => e.stopPropagation()}>
        <div className="client-guide-top">
          <span className="client-guide-badge">{hint.badge || "PARA VOCÊ"}</span>
          <button type="button" className="client-guide-close" aria-label="Fechar" onClick={() => dismiss(hint.id)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="client-guide-body">
          <h2 id="client-guide-title" className="client-guide-title">{hint.title}</h2>
          <p className="client-guide-text">{hint.body}</p>
          <button type="button" className="client-guide-cta" onClick={() => goCta(hint)}>
            {hint.cta_label || "Ver agora"}
          </button>
          <button type="button" className="client-guide-later" onClick={() => dismiss(hint.id)}>
            Agora não
          </button>
        </div>
      </div>
    </div>
  );
}
